import React from "react";
import localize, { LocKeys } from "../../constants/localizations";
import { permissions } from "../../constants/permissionConstants";
import { getPermissions, tokenParams } from "../../utils/local-storage.util";
import { isBimify, isLoggedUserGuest } from "../../utils/permission/user";

export const ticketItemKey = {
	offer: "offer",
	building: "building",
	job: "job",
};

export const ticketAssigneeKeyConstants = {
	company: "company",
	user: "user",
	group: "group",
};

/**
 * Discipline groups a job ticket can be assigned to as a whole (EXE-319).
 * Values match the API's `assignedGroup`; the BE stores the group on the
 * ticket and resolves its members at read time, so membership changes after
 * assignment are picked up automatically.
 */
export const jobTicketGroups = {
	normalisation: "normalisation",
	customisation: "customisation",
	qa: "qa",
};

export const getAssignedGroupLabel = (group) => {
	switch (group) {
		case jobTicketGroups.normalisation:
			return localize(LocKeys.NORMALISATION);
		case jobTicketGroups.customisation:
			return localize(LocKeys.CUSTOMISATION);
		case jobTicketGroups.qa:
			return localize(LocKeys.QUALITY_CONTROL);
		default:
			return null;
	}
};

export const ticketPriorityConstants = {
	low: "low",
	high: "high",
	medium: "medium",
};

export const getPriorityLabel = (priority) => {
	switch (priority) {
		case ticketPriorityConstants.high:
			return localize(LocKeys.HIGH);

		case ticketPriorityConstants.medium:
			return localize(LocKeys.MEDIUM);

		default:
			return localize(LocKeys.LOW);
	}
};

export const getItemLabel = (item) => {
	switch (item) {
		case ticketItemKey.offer:
			return localize(LocKeys.OFFER);

		case ticketItemKey.building:
			return localize(LocKeys.BUILDING);

		default:
			return localize(LocKeys.JOB);
	}
};

export const getItemLink = (item) => {
	switch (item) {
		case ticketItemKey.offer:
			return "offers";

		case ticketItemKey.building:
			return "buildings";

		case ticketItemKey.job:
			return "jobs";

		default:
			return "offers";
	}
};

export const getItemValue = (item, itemName, tempItem) => {
	if (item === tempItem) {
		return itemName?.value || null;
	}

	return null;
};

/**
 * Assignee options for a job ticket (2026-07-06 sync rules, EXE-319).
 *
 * Rule 1 — individual assignment is restricted to the assigner's own group:
 * discipline users (Normaliser/Customiser/QA) only see their own group's
 * members, while Admin/BO/PL (`canAssignAllJobUsers`) see every group.
 * Rule 2 — group-level assignment: every actor may target the Bimify group or
 * any of the three discipline groups. The Bimify group rides the existing
 * company-assignee mechanism (`company_<id>` → `companyIds`); discipline
 * groups map onto the API's `assignedGroup` (`group_<name>`), whose members
 * the BE resolves at read time. A group with no members on the job is not
 * offered because there would be nobody to see the ticket today.
 *
 * Pure function of its inputs so the permission matrix is unit-testable.
 *
 * @param {Object} args
 * @param {Immutable.Map} [args.bimifyCompany] - jobCompanies.bimifyCompany
 * @param {Immutable.Map} [args.supplierUsers] - jobUsers.supplierUsers
 * @param {boolean} [args.canAssignAllJobUsers] - true for Admin/BO/PL
 * @param {boolean} [args.isNormalizer]
 * @param {boolean} [args.isCustomizer]
 * @param {boolean} [args.isQA]
 * @returns {Array} autocomplete options
 */
export const buildJobTicketAssigneeOptions = ({
	bimifyCompany = null,
	supplierUsers = null,
	canAssignAllJobUsers = false,
	isNormalizer = false,
	isCustomizer = false,
	isQA = false,
} = {}) => {
	const options = [];

	const groupSubHeader = (
		<div className="dropdown-subheader dropdown-subheader--border body-font--small semi">
			{localize(LocKeys.GROUP)}
		</div>
	);

	// Group-level assignees first — visible to every job-ticket creator.
	if (bimifyCompany) {
		options.push({
			subHeader: groupSubHeader,
			key: ticketAssigneeKeyConstants.company,
			value: `${ticketAssigneeKeyConstants.company}_${+bimifyCompany.get("id")}`,
			label: bimifyCompany.get("name"),
			note: localize(LocKeys.GROUP),
		});
	}

	const groupMemberKey = {
		[jobTicketGroups.normalisation]: "normalisationUsers",
		[jobTicketGroups.customisation]: "customisationUsers",
		[jobTicketGroups.qa]: "qaUsers",
	};

	Object.values(jobTicketGroups).forEach((group) => {
		const members = supplierUsers?.get(groupMemberKey[group]);
		if (!members || members.size === 0) return;

		options.push({
			subHeader: groupSubHeader,
			key: ticketAssigneeKeyConstants.group,
			value: `${ticketAssigneeKeyConstants.group}_${group}`,
			label: getAssignedGroupLabel(group),
			note: localize(LocKeys.GROUP),
		});
	});

	// Push a user group's members under a shared subheader.
	const pushGroupUsers = (list, groupNote) => {
		if (!list || list.size === 0) return;
		const subHeader = (
			<div className="dropdown-subheader dropdown-subheader--border body-font--small semi">
				{groupNote}
			</div>
		);
		list.forEach((user) => {
			options.push({
				subHeader,
				key: ticketAssigneeKeyConstants.user,
				value: `${ticketAssigneeKeyConstants.user}_${+user.getIn(["assigneeUser", "id"])}`,
				label: `${user.getIn(["assigneeUser", "firstName"])} ${user.getIn(["assigneeUser", "lastName"])}`,
				note: groupNote,
			});
		});
	};

	if (canAssignAllJobUsers && supplierUsers) {
		const bimifySubHeader = (
			<div className="dropdown-subheader dropdown-subheader--border body-font--small semi">
				{`${bimifyCompany?.get("name") || ""} ${localize(LocKeys.USERS)}`}
			</div>
		);
		const contactPerson = supplierUsers?.get("contactPerson") || null;
		const projectLead = supplierUsers?.get("projectLead") || null;

		contactPerson &&
			options.push({
				subHeader: bimifySubHeader,
				key: ticketAssigneeKeyConstants.user,
				value: `${ticketAssigneeKeyConstants.user}_${+contactPerson?.get("id")}`,
				label: `${contactPerson?.get("firstName")} ${contactPerson?.get("lastName")}`,
				note: localize(LocKeys.BUSINESS_OWNER_LABEL),
			});
		projectLead &&
			options.push({
				subHeader: bimifySubHeader,
				key: ticketAssigneeKeyConstants.user,
				value: `${ticketAssigneeKeyConstants.user}_${+projectLead?.get("id")}`,
				label: `${projectLead?.get("firstName")} ${projectLead?.get("lastName")}`,
				note: localize(LocKeys.PROJECT_LEAD_LABEL),
			});
	}

	if (canAssignAllJobUsers || isNormalizer) {
		pushGroupUsers(
			supplierUsers?.get("normalisationUsers"),
			localize(LocKeys.NORMALISATION)
		);
	}
	if (canAssignAllJobUsers || isCustomizer) {
		pushGroupUsers(
			supplierUsers?.get("customisationUsers"),
			localize(LocKeys.CUSTOMISATION)
		);
	}
	if (canAssignAllJobUsers || isQA) {
		pushGroupUsers(
			supplierUsers?.get("qaUsers"),
			localize(LocKeys.QUALITY_CONTROL_LABEL)
		);
	}

	return options;
};

/**
 * Turn the single `assignee` form value into the assignee fields the ticket
 * API accepts. A `group_<name>` assignee is sent as `assignedGroup` — the BE
 * resolves the group's members itself whenever they are needed.
 *
 * @param {Object} args
 * @param {string} [args.assignee] - tempTicket.assignee (`user_1`, `company_10`, `group_qa`)
 * @param {string} [args.assigneeKey] - tempTicket.assigneeKey
 * @returns {{userIds: number[], companyIds: number[], assignedGroup: string|null}}
 */
export const resolveTicketAssignee = ({
	assignee = null,
	assigneeKey = null,
} = {}) => {
	if (!assignee) {
		return { userIds: [], companyIds: [], assignedGroup: null };
	}

	const target = assignee.toString().split("_")[1];

	return {
		userIds: assigneeKey === ticketAssigneeKeyConstants.user ? [+target] : [],
		companyIds:
			assigneeKey === ticketAssigneeKeyConstants.company ? [+target] : [],
		assignedGroup:
			assigneeKey === ticketAssigneeKeyConstants.group ? target : null,
	};
};

export const getTicketItemOptions = () => {
	const loggedUser = tokenParams();
	const isBimifyAdmin = loggedUser.isBimifyAdmin;
	const loggedUserPermissions = getPermissions(); //loggedUser.permissions;
	const itemOptions = [];

	if (
		isBimifyAdmin ||
		loggedUserPermissions?.offers?.permissions?.includes(permissions.view)
	) {
		itemOptions.push({
			value: ticketItemKey.offer,
			label: localize(LocKeys.OFFER),
		});
	}

	if (
		isBimifyAdmin ||
		loggedUserPermissions?.buildings?.permissions?.includes(permissions.view) ||
		loggedUserPermissions?.jobs?.permissions?.includes(permissions.view)
	) {
		itemOptions.push({
			value: ticketItemKey.building,
			label: localize(LocKeys.BUILDING),
		});
	}

	// Job tickets are Bimify-internal: only non-guest Bimify roles (Admin, BO,
	// PL, Normaliser, Customiser, QA) can create them — never resellers/clients.
	if (isBimify() && !isLoggedUserGuest()) {
		itemOptions.push({
			value: ticketItemKey.job,
			label: localize(LocKeys.JOB),
		});
	}

	return itemOptions;
};
